import { type FC, type ReactNode, useId } from "react"

import {
	type CommunityAccess,
	type CommunityChannelProductAccess,
	type CommunityChannelProductType,
	communityAccess,
	communityChannelProductTypes,
} from "@forento/core/models/community"
import { getCommunityAccessDisplayName, isCommunityAccessHigher } from "@forento/core/utilities/community"
import Tooltip from "@forento/ui/components/Tooltip"

import Button from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import { DeleteIcon } from "~/components/Icon"
import InputLabel from "~/components/InputLabel"
import { query } from "~/utilities/trpc"

export type MembershipAccess = { membershipId: number; access: CommunityAccess }
export type ProductAccess = CommunityChannelProductAccess
type ChannelProduct = { type: CommunityChannelProductType; id: number; title: string; isFree: boolean }
type Membership = { id: number; title: string }

const productTypeNames: Record<CommunityChannelProductType, { singular: string; plural: string }> = {
	course: { singular: "Course", plural: "Courses" },
	downloadable: { singular: "Downloadable", plural: "Downloadables" },
	video: { singular: "Video", plural: "Videos" },
	event: { singular: "Event", plural: "Events" },
}

// Free products only grant access once used, so their rows say what counts.
const freeProductHints: Record<CommunityChannelProductType, string> = {
	course: "Free, counts once they start it",
	downloadable: "Free, counts once they download it",
	video: "Free, counts once they watch it",
	event: "Free, counts once they sign up",
}

export function useChannelProducts(): { data: ChannelProduct[] | undefined; error: boolean } {
	const courses = query.course.list.useQuery()
	const downloadables = query.downloadable.list.useQuery()
	const videos = query.video.list.useQuery()
	const events = query.event.list.useQuery()

	if (courses.error || downloadables.error || videos.error || events.error) return { data: undefined, error: true }
	if (!courses.data || !downloadables.data || !videos.data || !events.data) return { data: undefined, error: false }
	const isFree = (product: { price: object | null; isManualOnly: boolean }) =>
		product.price === null && !product.isManualOnly
	return {
		data: [
			...courses.data.map(x => ({ type: "course" as const, id: x.id, title: x.title, isFree: isFree(x) })),
			...downloadables.data.map(x => ({
				type: "downloadable" as const,
				id: x.id,
				title: x.name,
				isFree: isFree(x),
			})),
			...videos.data.map(x => ({ type: "video" as const, id: x.id, title: x.name, isFree: isFree(x) })),
			...events.data.map(x => ({ type: "event" as const, id: x.id, title: x.title, isFree: isFree(x) })),
		],
		error: false,
	}
}

type Props = {
	memberships: Membership[]
	products: ChannelProduct[]
	defaultAccess: CommunityAccess | null
	membershipAccess: MembershipAccess[]
	productAccess: ProductAccess[]
	onDefaultAccessChange(value: CommunityAccess | null): void
	onMembershipAccessChange(value: MembershipAccess[]): void
	onProductAccessChange(value: ProductAccess[]): void
}
const ChannelAccessInputs: FC<Props> = ({
	memberships,
	products,
	defaultAccess,
	membershipAccess,
	productAccess,
	onDefaultAccessChange,
	onMembershipAccessChange,
	onProductAccessChange,
}) => {
	const id = useId()

	const higherAccess = communityAccess.filter(access => isCommunityAccessHigher(access, defaultAccess))
	const isSame = (a: { type: string; id: number }, b: { type: string; id: number }) =>
		a.type === b.type && a.id === b.id

	const availableMemberships = memberships.filter(
		membership => !membershipAccess.some(access => access.membershipId === membership.id),
	)
	const availableProducts = communityChannelProductTypes.flatMap(type =>
		products.filter(product => product.type === type && !productAccess.some(access => isSame(access, product))),
	)
	const canAddRules = higherAccess.length > 0 && availableMemberships.length + availableProducts.length > 0

	function ruleItems(access: CommunityAccess) {
		const items = higherAccess.map(x => ({ id: x, title: getCommunityAccessDisplayName(x) }))
		// A rule at or below "Everyone" grants nothing, such as after the creator raised the default.
		return isCommunityAccessHigher(access, defaultAccess)
			? items
			: [{ id: "no-effect", title: "Same as everyone", isDisabled: true }, ...items]
	}
	const ruleValue = (access: CommunityAccess) =>
		isCommunityAccessHigher(access, defaultAccess) ? access : "no-effect"

	return (
		<div>
			<InputLabel htmlFor={`${id}-everyone`}>Who can access this channel</InputLabel>
			<p className="text-light mb-3 text-sm">People get the highest level from any rule that applies to them.</p>
			<div className="divide-component-border border-component-border divide-y rounded-xl border">
				<AccessRow title="Everyone" subtitle="All students in your community" inputId={`${id}-everyone`}>
					<Dropdown
						id={`${id}-everyone`}
						items={[
							{ id: "none", title: "No access" },
							...communityAccess.map(access => ({
								id: access,
								title: getCommunityAccessDisplayName(access),
							})),
						]}
						selectedItemId={defaultAccess ?? "none"}
						onChange={value => onDefaultAccessChange(communityAccess.find(x => x === value) ?? null)}
					/>
				</AccessRow>
				{membershipAccess.map(access => {
					const inputId = `${id}-membership-${access.membershipId}`
					return (
						<AccessRow
							key={inputId}
							title={memberships.find(x => x.id === access.membershipId)?.title ?? "Deleted membership"}
							subtitle="Membership"
							inputId={inputId}
							onRemove={() =>
								onMembershipAccessChange(
									membershipAccess.filter(x => x.membershipId !== access.membershipId),
								)
							}
						>
							<Dropdown
								id={inputId}
								items={ruleItems(access.access)}
								selectedItemId={ruleValue(access.access)}
								onChange={value => {
									const newAccess = communityAccess.find(x => x === value)
									if (newAccess === undefined) return
									onMembershipAccessChange(
										membershipAccess.map(x =>
											x.membershipId === access.membershipId ? { ...x, access: newAccess } : x,
										),
									)
								}}
							/>
						</AccessRow>
					)
				})}
				{productAccess.map(access => {
					const inputId = `${id}-${access.type}-${access.id}`
					// Saved access keeps its own title, so it still shows when its product is missing from the lists,
					// such as a video on a plan without videos, and the creator can remove it.
					const product = products.find(product => isSame(product, access))
					return (
						<AccessRow
							key={inputId}
							title={product?.title ?? access.title}
							subtitle={
								product?.isFree
									? `${productTypeNames[access.type].singular} · ${freeProductHints[access.type]}`
									: productTypeNames[access.type].singular
							}
							inputId={inputId}
							onRemove={() => onProductAccessChange(productAccess.filter(x => !isSame(x, access)))}
						>
							<Dropdown
								id={inputId}
								items={ruleItems(access.access)}
								selectedItemId={ruleValue(access.access)}
								onChange={value => {
									const newAccess = communityAccess.find(x => x === value)
									if (newAccess === undefined) return
									onProductAccessChange(
										productAccess.map(x => (isSame(x, access) ? { ...x, access: newAccess } : x)),
									)
								}}
							/>
						</AccessRow>
					)
				})}
			</div>
			{higherAccess.length === 0 && membershipAccess.length + productAccess.length > 0 && (
				<p className="text-light mt-2 text-sm">
					Everyone can already post, so memberships and products don't add anything.
				</p>
			)}
			{canAddRules && (
				<label htmlFor={`${id}-add`} className="sr-only">
					Add a membership or product
				</label>
			)}
			{canAddRules && (
				<Dropdown
					id={`${id}-add`}
					className="mt-3"
					items={[
						{
							id: "none",
							title: availableMemberships.length > 0 ? "Add a membership or product" : "Add a product",
						},
						...availableMemberships.map(membership => ({
							id: `membership-${membership.id}`,
							title: membership.title,
							group: { id: "membership", title: "Memberships" },
						})),
						...availableProducts.map(product => ({
							id: `${product.type}-${product.id}`,
							title: product.title,
							group: { id: product.type, title: productTypeNames[product.type].plural },
						})),
					]}
					selectedItemId="none"
					onChange={selectedId => {
						const membership = availableMemberships.find(x => `membership-${x.id}` === selectedId)
						if (membership !== undefined) {
							onMembershipAccessChange([
								...membershipAccess,
								{ membershipId: membership.id, access: higherAccess[0] },
							])
							return
						}
						const product = availableProducts.find(x => `${x.type}-${x.id}` === selectedId)
						if (product === undefined) return
						onProductAccessChange([
							...productAccess,
							{ type: product.type, id: product.id, title: product.title, access: higherAccess[0] },
						])
					}}
				/>
			)}
		</div>
	)
}

const AccessRow: FC<{
	title: string
	subtitle: string
	inputId: string
	onRemove?(): void
	children: ReactNode
}> = ({ title, subtitle, inputId, onRemove, children }) => (
	<div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
		<label htmlFor={inputId} className="flex min-w-0 flex-1 basis-40 flex-col">
			<span title={title} className="truncate text-sm font-semibold">
				{title}
			</span>
			<span className="text-light text-xs">{subtitle}</span>
		</label>
		{/* On narrow screens the dropdown takes its own full-width line. */}
		<div className="order-last w-full sm:order-none sm:w-68 sm:shrink-0">{children}</div>
		{onRemove ? (
			<Tooltip tooltip="Remove">
				<Button className="size-6 shrink-0" onClick={onRemove}>
					<DeleteIcon className="text-danger size-full" />
					<span className="sr-only">Remove access for {title}</span>
				</Button>
			</Tooltip>
		) : (
			<div className="hidden size-6 shrink-0 sm:block" />
		)}
	</div>
)

export default ChannelAccessInputs
